import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { OrganizationalPowerAnalysisStatus } from "@/graphql/generated/types";
import { isAnalyzableDocument } from "@/lib/analyzable-documents";
import { createTaskPool } from "@/lib/task-pool";
import { userErrorMessage } from "@/lib/user-error";
import {
  ensureOrganizationalRecord,
  fileOrganizationalRecordDocument,
  type OrganizationalRecordFile,
} from "@/pages/organizational-record/organizational-record-filing";
import {
  applyOrganizationalPowerAnalysis,
  awaitOrganizationalPowerAnalysis,
  startOrganizationalPowerAnalysis,
  type OrganizationalPowerAnalysis,
  type OrganizationalPowerScope,
} from "@/pages/organizational-record/organizational-power-analysis";

/**
 * Analyses running at once, chosen not measured (@adr SPARK-0049-roster-import-power-analysis-goes-through-the-organizational-record@1).
 * Each one scores all 30 powers in parallel inside document-extraction, which shares its gateway
 * permits with the import's own profile extraction; more than two would slow the import into its
 * BFF timeout and push queued analyses past genesis' hard timeout.
 */
export const MAX_PARALLEL_ANALYSES = 2;

export type ImportPowerRow =
  /** File type the analysis cannot read; nothing is filed. */
  | { status: "unsupported" }
  | { status: "queued" | "filing" | "running"; personId: string }
  | { status: "failed"; personId: string; error?: string }
  /** `error`: the last apply attempt failed; the proposal is still there to retry. */
  | { status: "ready" | "applying"; personId: string; scope: OrganizationalPowerScope; analysis: OrganizationalPowerAnalysis; error?: string }
  | { status: "empty" | "applied"; personId: string; scope: OrganizationalPowerScope; analysis: OrganizationalPowerAnalysis };

export type ImportPowerStatus = ImportPowerRow["status"];

const ACTIVE: ReadonlySet<ImportPowerStatus> = new Set(["queued", "filing", "running", "applying"]);

function settledRow(analysis: OrganizationalPowerAnalysis, personId: string, scope: OrganizationalPowerScope): ImportPowerRow {
  switch (analysis.status) {
    case OrganizationalPowerAnalysisStatus.Ready:
      return { status: analysis.traits.length ? "ready" : "empty", personId, scope, analysis };
    case OrganizationalPowerAnalysisStatus.Applied:
      return { status: "applied", personId, scope, analysis };
    default:
      return { status: "failed", personId };
  }
}

/**
 * Power analysis for imported people (@adr SPARK-0049-roster-import-power-analysis-goes-through-the-organizational-record@1):
 * per person the CV goes into the organizational record, the record's analysis runs on it, and the
 * reviewed proposals are applied on request. Rows are keyed by the caller, one per imported file.
 * State lives in this page only: leaving it stops watching, while running analyses finish on the server.
 */
export function useImportPowerAnalysis() {
  const client = useApolloClient();
  const intl = useIntl();
  const [rows, setRows] = useState<Readonly<Record<string, ImportPowerRow>>>({});
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const pool = useMemo(() => createTaskPool(MAX_PARALLEL_ANALYSES), []);
  // One controller per batch: reset() and unmount stop watching, queued work never starts.
  const controller = useRef(new AbortController());
  useEffect(() => {
    const batch = controller;
    batch.current = new AbortController();
    // The batch at unmount, which reset() may have replaced since mount.
    return () => batch.current.abort();
  }, []);

  const update = useCallback((key: string, row: ImportPowerRow) => setRows(previous => ({ ...previous, [key]: row })), []);

  const reset = useCallback(() => {
    controller.current.abort();
    controller.current = new AbortController();
    setRows({});
    setSelection(new Set());
  }, []);

  const enqueue = useCallback((key: string, personId: string, file: OrganizationalRecordFile) => {
    if (!isAnalyzableDocument(file.name, file.mimeType)) {
      update(key, { status: "unsupported" });
      return;
    }
    const { signal } = controller.current;
    update(key, { status: "queued", personId });
    void pool.run(async () => {
      if (signal.aborted) return;
      try {
        update(key, { status: "filing", personId });
        // Every await may outlive the batch (reset, unmount): no further step and no row update after it.
        const recordId = await ensureOrganizationalRecord(client, personId);
        if (signal.aborted) return;
        const document = await fileOrganizationalRecordDocument(client, recordId, file);
        if (signal.aborted) return;
        const scope = { recordId, personId };
        const started = await startOrganizationalPowerAnalysis(client, scope, [document.id]);
        if (signal.aborted) return;
        update(key, { status: "running", personId });
        const settled = await awaitOrganizationalPowerAnalysis(client, started, scope, { signal });
        if (signal.aborted) return;
        update(key, settledRow(settled, personId, scope));
      } catch (failure) {
        if (signal.aborted) return;
        update(key, { status: "failed", personId, error: userErrorMessage(intl, failure) });
      }
    });
  }, [client, intl, pool, update]);

  const readyKeys = useMemo(() => Object.entries(rows).filter(([, row]) => row.status === "ready").map(([key]) => key), [rows]);
  // A row leaves the selection by leaving "ready" (applied), without a second bookkeeping step.
  const selected = useMemo(() => new Set(readyKeys.filter(key => selection.has(key))), [readyKeys, selection]);
  const active = useMemo(() => Object.values(rows).some(row => ACTIVE.has(row.status)), [rows]);
  const applying = useMemo(() => Object.values(rows).some(row => row.status === "applying"), [rows]);

  const toggle = useCallback((key: string) => setSelection(previous => {
    const next = new Set(previous);
    if (!next.delete(key)) next.add(key);
    return next;
  }), []);
  const setAllSelected = useCallback((select: boolean) => setSelection(select ? new Set(readyKeys) : new Set()), [readyKeys]);

  // One apply per person, one after another: each is its own transaction in genesis, and a
  // failure stays on its row instead of stopping the others.
  const applySelected = useCallback(async () => {
    // The selection belongs to this batch; leaving the page stops the remaining applies.
    const { signal } = controller.current;
    for (const key of selected) {
      if (signal.aborted) return;
      const row = rows[key];
      if (row?.status !== "ready") continue;
      update(key, { ...row, status: "applying", error: undefined });
      try {
        const applied = await applyOrganizationalPowerAnalysis(client, row.analysis.id, row.scope);
        if (signal.aborted) return;
        update(key, { status: "applied", personId: row.personId, scope: row.scope, analysis: applied });
      } catch (failure) {
        if (signal.aborted) return;
        update(key, { ...row, status: "ready", error: userErrorMessage(intl, failure) });
      }
    }
  }, [client, intl, rows, selected, update]);

  return { rows, enqueue, reset, active, applying, readyKeys, selected, toggle, setAllSelected, applySelected };
}
